import React, { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from 'src/enviroment/lib/utils'
import Typography from 'src/components/atoms/Typography'
import { ProjectStatusLayer } from 'src/types/projects'
import { LayerUploadStatus } from 'src/types/layer'

type Props = {
  layers: ProjectStatusLayer[]
  aoiLayerReady: boolean
  copaStatus?: LayerUploadStatus
}

const StatusDot: React.FC<{ ready: boolean }> = ({ ready }) => (
  <div className={cn('relative h-4 w-4 min-w-[1rem] shrink-0', !ready && 'animate-pulse')}>
    {ready ? (
      <div className="absolute inset-0 origin-center animate-fill-in rounded-full bg-light-green-500" />
    ) : (
      <div className="absolute inset-0 animate-spin rounded-full border-2 border-gray-400 border-r-transparent" />
    )}
  </div>
)

const LayerLoadingLegend: React.FC<Props> = ({ layers, aoiLayerReady, copaStatus }) => {
  const [open, setOpen] = useState(true)

  const visibleLayers = layers.filter(
    (l) => l.status !== LayerUploadStatus.ERROR && l.status !== LayerUploadStatus.NO_DATA,
  )
  const copaSelectingLayers = copaStatus === LayerUploadStatus.PROCESSING && visibleLayers.length === 0

  return (
    <div className="flex max-h-full w-[280px] flex-col overflow-hidden rounded-lg border border-gray-650 bg-gray-900 p-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full shrink-0 items-center justify-between"
      >
        <Typography variant="base-bold" element="span" className="text-light-green-50">
          Layers
        </Typography>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      <div
        className={cn(
          'min-h-0 transition-all duration-200',
          open ? 'opacity-100' : 'max-h-0 overflow-hidden opacity-0',
        )}
      >
        <div className="mt-2 flex flex-col overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div
            className={cn('flex items-center gap-2 pt-4', (visibleLayers.length > 0 || copaSelectingLayers) && 'pb-4')}
          >
            <StatusDot ready={aoiLayerReady} />
            <Typography
              variant="sm-medium"
              element="span"
              className={cn('truncate', aoiLayerReady ? 'text-gray-50' : 'text-gray-600')}
            >
              Project Area
            </Typography>
          </div>

          {copaSelectingLayers && (
            <>
              <hr className="border-t border-gray-600" />
              <div className="flex items-center gap-2 pt-4">
                <StatusDot ready={false} />
                <Typography variant="sm-medium" element="span" className="truncate text-gray-600">
                  COPA is creating your data cube…
                </Typography>
              </div>
            </>
          )}

          {visibleLayers.map((layer, i) => {
            const isCompleted = layer.status === LayerUploadStatus.COMPLETED
            return (
              <React.Fragment key={layer.layerId}>
                <hr className="border-t border-gray-600" />
                <div className={cn('flex items-center gap-2 pt-4', i < visibleLayers.length - 1 && 'pb-4')}>
                  <StatusDot ready={isCompleted} />
                  <Typography
                    variant="sm-medium"
                    element="span"
                    className={cn('truncate', isCompleted ? 'text-gray-50' : 'text-gray-600')}
                  >
                    {layer.fileName}
                  </Typography>
                </div>
              </React.Fragment>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default LayerLoadingLegend
